import { removeHtml } from './TextUtils';

export const cloneAndProcessTable = (table: HTMLTableElement): HTMLTableElement => {
    const clonedTable = table.cloneNode(true) as HTMLTableElement;
    const thead = clonedTable.querySelector('thead');

    if (thead) {
        const seenHeaders = new Set<string>();
        const headerRows = Array.from(thead.rows);

        headerRows.forEach((row) => {
            const rowContent = Array.from(row.cells)
                .map((cell) => cell.innerHTML.trim())
                .join('|');

            if (seenHeaders.has(rowContent)) {
                row.remove(); // Remove duplicate row
            } else {
                seenHeaders.add(rowContent);
            }
        });
    }

    return clonedTable;
};

const processTables = (element: HTMLElement): string => {
    const tables = element.querySelectorAll('table');
    tables.forEach((table) => {
        const processedTable = cloneAndProcessTable(table);
        table.replaceWith(processedTable);
    });

    return element.outerHTML;
};

const tableToMarkdown = (table: HTMLTableElement): string => {
    let markdown = '';
    const rows = Array.from(table.rows);

    rows.forEach((row, rowIndex) => {
        const cells = Array.from(row.cells);
        const rowContent = cells.map((cell) => cell.textContent?.trim() || '').join(' | ');

        markdown += `| ${rowContent} |\n`;

        if (rowIndex === 0) {
            const headerSeparator = cells.map(() => '---').join(' | ');
            markdown += `| ${headerSeparator} |\n`;
        }
    });

    return markdown;
};

export const copyOnClick = async (id: string, setComplete: (complete: boolean) => void, content?: string) => {
    try {
        const element = document.getElementById(id);

        if (!element) {
            setComplete(false);
            return;
        }

        let htmlContentToCopy = '';
        let textContentToCopy = '';
        let markdownContentToCopy = '';

        // Clone the entire content of the element
        const clonedElement = element.cloneNode(true) as HTMLElement;

        // Process tables within the cloned element
        htmlContentToCopy = processTables(clonedElement);

        // Convert HTML to plain text
        textContentToCopy = removeHtml(htmlContentToCopy);
        markdownContentToCopy = content || textContentToCopy;

        const clipboardItemData = {
            ['text/plain']: new Blob([markdownContentToCopy], { type: 'text/plain' }),
            ['text/html']: new Blob([htmlContentToCopy], { type: 'text/html' }),
        };
        const clipboardItem = new ClipboardItem(clipboardItemData);

        await navigator.clipboard.write([clipboardItem]);
        setComplete(true);
        setTimeout(() => setComplete(false), 2000);
    } catch (error) {
        console.error('Failed to copy content to clipboard:', error);
        setComplete(false);
    }
};

export const copyTableContent = async (id: string, setComplete: (complete: boolean) => void) => {
    try {
        const element = document.getElementById(id);

        if (!element) {
            setComplete(false);
            return;
        }

        const table = element.querySelector('table') as HTMLTableElement;
        if (!table) {
            setComplete(false);
            return;
        }

        const clonedTable = cloneAndProcessTable(table);
        const htmlContentToCopy = clonedTable.outerHTML;
        const textContentToCopy = removeHtml(htmlContentToCopy);
        const markdownContentToCopy = tableToMarkdown(clonedTable) || textContentToCopy;

        const clipboardItemData = {
            ['text/plain']: new Blob([markdownContentToCopy], { type: 'text/plain' }),
            ['text/html']: new Blob([htmlContentToCopy], { type: 'text/html' }),
        };
        const clipboardItem = new ClipboardItem(clipboardItemData);

        await navigator.clipboard.write([clipboardItem]);
        setComplete(true);
        setTimeout(() => setComplete(false), 2000);
    } catch (error) {
        console.error('Failed to copy table content to clipboard:', error);
        setComplete(false);
    }
};

export const copyImageOnClick = async (content, setComplete) => {
    try {
        // Convert the base64 data URL to a Blob
        const binaryData = atob(content);
        const byteNumbers = new Array(binaryData.length).fill(0).map((_, i) => binaryData.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);

        // Create a Blob from the byte array
        const blob = new Blob([byteArray], { type: 'image/png' });

        // Use Clipboard API to copy the Blob
        await navigator.clipboard
            .write([
                new ClipboardItem({
                    [blob.type]: blob,
                }),
            ])
            .then(() => {
                setComplete(true);
                setTimeout(() => {
                    setComplete(false);
                }, 2000);
            });
    } catch (error) {
        console.error('Failed to copy image: ', error);
        setComplete(false);
    }
};

export const copyUrlToClipboard = (url: string, setComplete) => {
    // Use the Clipboard API to write the URL to the clipboard
    navigator.clipboard
        .writeText(url)
        .then(() => {
            setComplete(true);
        })
        .catch((err) => {
            console.log(`Failed to copy URL to clipboard: ${err}`);
            setComplete(false);
        });
};
